// NSARA Stores V2 — workspace d'une boutique (sections 7 et 8).
//
// Remplace l'ancien "BoutiqueDetail" à 8 onglets techniques. L'écran s'ouvre sur
// le titre de la boutique, qui EST le sélecteur de boutique (ShopSwitcher),
// suivi de la navigation Aperçu / Produits / Commandes / Clients / Apparence /
// Paramètres et des actions contextuelles (Voir ma boutique, Ajouter un produit).
//
// ISOLATION (section 3): l'onglet actif reçoit businessId + shopId et chaque
// panneau est monté avec `key={shopId}` — changer de boutique remonte donc
// complètement le contenu, ce qui garantit qu'aucun état asynchrone de la
// boutique précédente ne survit (aucun produit, aucune commande résiduelle).
//
// CONTEXTE: la boutique ouverte devient aussi la boutique active du Provider
// partagé (BusinessContext), donc les modules Ventes / Inventaire / Commandes
// suivent immédiatement, sans rechargement de page ni reconnexion.
//
// Les boutiques ne sont plus rechargées ici via un getBusiness()+getShops()
// local: elles viennent du Provider partagé, qui les charge une seule fois par
// session (voir src/contexts/BusinessContext.js).
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { ExternalLink, Plus, Store } from 'lucide-react';
import { useBusinessContext } from '../hooks/useBusinessContext';
import ShopSwitcher from './boutiques/ShopSwitcher';
import CreateShopDialog from './boutiques/CreateShopDialog';
import StoreOverviewTab from './boutiques/StoreOverviewTab';
import StoreProductsTab from './boutiques/StoreProductsTab';
import StoreOrdersTab from './boutiques/StoreOrdersTab';
import StoreCustomersTab from './boutiques/StoreCustomersTab';
import StoreAppearanceTab from './boutiques/StoreAppearanceTab';
import StoreSettingsTab from './boutiques/StoreSettingsTab';
import { buildPublicStoreUrl } from './boutiques/storeUtils';
import '../styles/stores.css';

export const STORE_TABS = [
  { id: 'apercu', label: 'Aperçu' },
  { id: 'produits', label: 'Produits' },
  { id: 'commandes', label: 'Commandes' },
  { id: 'clients', label: 'Clients' },
  { id: 'apparence', label: 'Apparence' },
  { id: 'parametres', label: 'Paramètres' }
];

export const isStoreTab = (value) => STORE_TABS.some((tab) => tab.id === value);

export default function BoutiqueDetail() {
  const { id: shopId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { businessId, shops, setActiveShopId, loading, error: contextError, reload } = useBusinessContext();

  const requestedTab = searchParams.get('tab');
  const tab = isStoreTab(requestedTab) ? requestedTab : 'apercu';
  const [createOpen, setCreateOpen] = useState(false);
  const [addProductSignal, setAddProductSignal] = useState(0);
  const [notice, setNotice] = useState('');

  // Bandeau de succès porté depuis "Mes boutiques" (state de navigation): la
  // création ouvre directement le workspace de la nouvelle boutique.
  useEffect(() => {
    const createdName = location.state?.createdShopName;
    if (!createdName) return;
    setNotice(`Boutique « ${createdName} » créée. Ajoutez vos produits pour la mettre en ligne.`);
    window.history.replaceState({}, '');
    window.setTimeout(() => setNotice(''), 5000);
  }, [location.state]);

  const shop = (shops || []).find((item) => item.id === shopId) || null;

  // La boutique ouverte devient la boutique active de toute l'application.
  useEffect(() => {
    if (shopId && (shops || []).some((item) => item.id === shopId)) setActiveShopId(shopId);
  }, [shopId, shops, setActiveShopId]);

  const openTab = (nextTab) => {
    if (nextTab === tab) return;
    const params = new URLSearchParams(searchParams);
    if (nextTab === 'apercu') params.delete('tab');
    else params.set('tab', nextTab);
    setSearchParams(params, { replace: true });
  };

  const addProduct = () => {
    openTab('produits');
    setAddProductSignal((value) => value + 1);
  };

  const handleCreated = async (createdShop) => {
    setCreateOpen(false);
    await reload();
    navigate(`/boutique/${createdShop.id}`, { state: { createdShopName: createdShop.name } });
  };

  const switchTo = (nextShopId) => {
    if (!nextShopId || nextShopId === shopId) return;
    // Pas de rechargement complet de l'application: la navigation React
    // remonte les panneaux (clé = shopId ci-dessous) et le Provider partagé
    // bascule la boutique active — Ventes / Inventaire / Commandes suivent
    // immédiatement, sans fuite de données de la boutique précédente.
    // L'onglet actif est conservé (Onglet courant + ?tab=...) : passer de la
    // boutique A à la boutique B ne jette pas le marchand hors de son travail.
    navigate(`/boutique/${nextShopId}${location.search}`);
  };

  if (loading && (shops || []).length === 0) {
    return (
      <div className="stores-page" aria-busy="true" aria-label="Chargement de la boutique">
        <div className="stores-skeleton-row" style={{ maxWidth: 320, height: 52 }} />
        <div className="stores-tabs" style={{ marginTop: 18, borderBottom: 'none' }}>
          {STORE_TABS.map((item) => <span key={item.id} className="stores-skeleton-line" style={{ width: 72, height: 34 }} />)}
        </div>
        <div className="stores-skeleton-row" />
        <div className="stores-skeleton-row" />
      </div>
    );
  }

  return (
    <div className="stores-page">
      {notice && <p className="stores-alert stores-alert--success" role="status">{notice}</p>}
      {contextError && <p className="stores-alert stores-alert--error" role="alert">{contextError}</p>}

      <div className="stores-workspace-bar">
        <ShopSwitcher
          shops={shops}
          activeShopId={shopId}
          onSelect={switchTo}
          onCreate={() => setCreateOpen(true)}
          disabled={loading}
        />
        <div className="stores-workspace-actions">
          <a
            className="stores-btn stores-btn--outline"
            href={shop ? buildPublicStoreUrl(shop.id) : '/boutique'}
            target="_blank"
            rel="noreferrer noopener"
            aria-disabled={!shop}
            onClick={(event) => { if (!shop) event.preventDefault(); }}
          >
            <ExternalLink size={15} aria-hidden="true" /> Voir ma boutique
          </a>
          <button type="button" className="stores-btn stores-btn--primary" onClick={addProduct} disabled={!shop}>
            <Plus size={16} aria-hidden="true" /> Ajouter un produit
          </button>
        </div>
      </div>

      <nav className="stores-tabs" aria-label="Sections de la boutique">
        {STORE_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? 'stores-tab is-active' : 'stores-tab'}
            aria-current={tab === item.id ? 'page' : undefined}
            onClick={() => openTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {!shop && !loading && (
        <div className="stores-empty">
          <span className="stores-empty-icon" aria-hidden="true"><Store size={22} /></span>
          <h3>Boutique introuvable</h3>
          <p>Cette boutique n’existe pas ou ne fait plus partie de votre entreprise.</p>
          <Link className="stores-btn stores-btn--primary" to="/boutique">Retour à Mes boutiques</Link>
        </div>
      )}

      {shop && (
        // Clé = boutique: changer de boutique DÉMONTE complètement l'onglet
        // actif — aucun état asynchrone (produits, commandes, formulaires) de
        // la boutique précédente ne peut survivre au changement.
        <div key={shop.id}>
          {tab === 'apercu' && (
            <StoreOverviewTab shop={shop} businessId={businessId} shopId={shop.id} onOpenOrders={() => openTab('commandes')} />
          )}
          {tab === 'produits' && (
            <StoreProductsTab businessId={businessId} shopId={shop.id} addSignal={addProductSignal} onCatalogChanged={reload} />
          )}
          {tab === 'commandes' && (
            <StoreOrdersTab businessId={businessId} shopId={shop.id} onConverted={reload} />
          )}
          {tab === 'clients' && (
            <StoreCustomersTab businessId={businessId} shopId={shop.id} />
          )}
          {tab === 'apparence' && (
            <StoreAppearanceTab shop={shop} onSaved={reload} />
          )}
          {tab === 'parametres' && (
            <StoreSettingsTab shop={shop} onSaved={reload} />
          )}
        </div>
      )}

      <CreateShopDialog
        open={createOpen}
        businessId={businessId}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}